import React from "react";
import { Link } from "react-router-dom";
import { Award, Calendar, MapPin, Mail } from "lucide-react";
import { personalInfo, skills } from "../data/mock";

const About = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-blue-900 py-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        {/* Header Section */}
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6 animate-slide-in">
            About Me
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto animate-fade-in">
            Passionate about ensuring software quality and delivering
            exceptional user experiences
          </p>
        </div>
        {/* Bio Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
          <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-xl border border-gray-200 dark:border-gray-700 animate-fade-in">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
                My Story
              </h2>
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                {personalInfo.bio}
              </p>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  <span className="text-gray-700 dark:text-gray-300">
                    <strong>{personalInfo.experience}</strong> in Quality
                    Assurance
                  </span>
                </div>
                <div className="flex items-center space-x-3">
                  <MapPin className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  <span className="text-gray-700 dark:text-gray-300">
                    Based in <strong>{personalInfo.location}</strong>
                  </span>
                </div>
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-pink-600 dark:text-pink-400" />
                  <span className="text-gray-700 dark:text-gray-300">
                    <strong>{personalInfo.email}</strong>
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-8 shadow-xl text-white animate-fade-in-delay">
              <Award className="w-12 h-12 mb-6" />
              <h3 className="text-2xl font-bold mb-4">Quality Excellence</h3>
              <p className="text-lg leading-relaxed mb-6">
                I adopt a holistic approach to software quality, combining
                thorough manual testing with scalable automation frameworks to
                ensure robust, reliable applications. My strategies focus on
                preventing critical issues before they reach production,
                improving overall software performance and user satisfaction.
              </p>
              <div className="grid grid-cols-2 gap-4 text-center">
                <div>
                  <div className="text-3xl font-bold">75%</div>
                  <div className="text-blue-100">Reduced Testing Time</div>
                </div>
                <div>
                  <div className="text-3xl font-bold">99.5%</div>
                  <div className="text-blue-100">Defect Detection Accuracy</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Skills Section */}
        <div className="mb-16">
          <h2 className="text-4xl font-bold text-center text-gray-900 dark:text-white mb-12 animate-fade-in">
            Skills & Expertise
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {skills.map((skillCategory, index) => (
              <div
                key={index}
                className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-xl border border-gray-200 dark:border-gray-700 hover:shadow-2xl transition-all duration-300 transform hover:scale-105 animate-fade-in-stagger"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                  {skillCategory.category}
                </h3>
                <div className="space-y-3">
                  {skillCategory.items.map((skill, skillIndex) => (
                    <div
                      key={skillIndex}
                      className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 dark:hover:from-blue-900/30 dark:hover:to-purple-900/30 transition-all duration-300"
                    >
                      <div className="w-2 h-2 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full"></div>
                      <span className="text-gray-700 dark:text-gray-300 font-medium">
                        {skill}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <section className="mb-16 p-8 rounded-3xl bg-blue-100 dark:bg-blue-900/30">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Freelance QA Consulting</h2>
          <p className="text-lg text-gray-700 dark:text-gray-300 mb-6">Alongside my professional roles, I have owned end-to-end QA for kibcar.com and pre-production quality coverage for a confidential web platform under NDA. My work spans UI, API and integration testing, k6 performance scenarios, Grafana monitoring and SonarQube analysis with Docker.</p>
          <Link to="/projects" className="font-semibold text-blue-700 dark:text-blue-300 underline">Explore my freelance QA projects</Link>
        </section>
        {/* Experience Section */}
        <div className="mb-16">
          <h2 className="text-4xl font-bold text-center text-gray-900 dark:text-white mb-12 animate-fade-in">
            Professional Experience
          </h2>

          <div className="timeline-container">
            {/* Experience Card 1 */}
            <div className="timeline-card animate-fade-in-stagger">
              <div className="timeline-card-content">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center text-2xl flex-shrink-0">
                    🏢
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                      QA Engineer
                    </h3>
                    <p className="text-lg text-purple-600 dark:text-purple-400 font-semibold">
                      International Bank of Azerbaijan
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 mb-6">
                  <Calendar className="w-4 h-4" />
                  <span>July 2025 - Present</span>
                </div>

                <ul className="space-y-3">
                  <li className="flex items-start gap-3 text-gray-700 dark:text-gray-300">
                    <span className="text-purple-600 dark:text-purple-400 text-xl flex-shrink-0">
                      ▹
                    </span>
                    <span>
                      Championed shift-left practices by embedding QA in early
                      design discussions, enabling early defect detection, and
                      strengthened test documentation by formalizing acceptance
                      criteria, creating reusable test suites, and standardizing
                      API schema and payload validation using Postman, Swagger,
                      Git, and JIRA.
                    </span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-700 dark:text-gray-300">
                    <span className="text-purple-600 dark:text-purple-400 text-xl flex-shrink-0">
                      ▹
                    </span>
                    <span>
                      Successfully delivered the international SWIFT switch to
                      MX format process modernization project within a rapid
                      two-week implementation cycle, ensuring full compliance
                      with global banking and security standards.
                    </span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-700 dark:text-gray-300">
                    <span className="text-purple-600 dark:text-purple-400 text-xl flex-shrink-0">
                      ▹
                    </span>
                    <span>
                      Contributed to the automation and restructuring of core
                      banking processes by supporting the implementation of the
                      new system architecture; conducted comprehensive risk
                      evaluations and performed end-to-end testing —over 50%
                      automated regression testing using Selenium, REST Assured,
                      Java, Jenkins, and SQL—driving measurable improvements in
                      yearly KPIs and release quality
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Experience Card 2 */}
            <div
              className="timeline-card animate-fade-in-stagger mt-8"
              style={{ animationDelay: "0.1s" }}
            >
              <div className="timeline-card-content">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center text-2xl flex-shrink-0">
                    💼
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                      QA Manual & Automation Mentor
                    </h3>
                    <p className="text-lg text-purple-600 dark:text-purple-400 font-semibold">
                      Matrix Academy
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 mb-6">
                  <Calendar className="w-4 h-4" />
                  <span>January 2024 - Present</span>
                </div>

                <ul className="space-y-3">
                  <li className="flex items-start gap-3 text-gray-700 dark:text-gray-300">
                    <span className="text-purple-600 dark:text-purple-400 text-xl flex-shrink-0">
                      ▹
                    </span>
                    <span>
                      Delivered 2-hour weekly mentor-led classes for Manual +
                      Automation (Java + Selenium + API), designing real
                      case-based tasks that helped students apply their
                      knowledge in practice and gain a deeper understanding of
                      QA workflows, including hands-on debugging and exercises.
                    </span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-700 dark:text-gray-300">
                    <span className="text-purple-600 dark:text-purple-400 text-xl flex-shrink-0">
                      ▹
                    </span>
                    <span>
                      Designed a full 16-week curriculum covering test design,
                      Java OOP for QA, Selenium architecture, API testing
                      (Postman + RestAssured), Git, SQL, and project-based
                      automation.
                    </span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-700 dark:text-gray-300">
                    <span className="text-purple-600 dark:text-purple-400 text-xl flex-shrink-0">
                      ▹
                    </span>
                    <span>
                      Guided learners through building complete automation
                      frameworks from scratch, ensuring each student completed
                      at least 2 production-grade test suites aligned with
                      industry standards.
                    </span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-700 dark:text-gray-300">
                    <span className="text-purple-600 dark:text-purple-400 text-xl flex-shrink-0">
                      ▹
                    </span>
                    <span>
                      Provided personalized feedback on code quality, test
                      strategy, and problem-solving, helping students achieve
                      70–90% score improvements across assessments.
                    </span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-700 dark:text-gray-300">
                    <span className="text-purple-600 dark:text-purple-400 text-xl flex-shrink-0">
                      ▹
                    </span>
                    <span>
                      Supported students in career development—CV refinement,
                      technical interview prep, and coding challenge
                      assistance—leading to multiple successful job placements.
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Experience Card 3 */}
            <div
              className="timeline-card animate-fade-in-stagger mt-8"
              style={{ animationDelay: "0.2s" }}
            >
              <div className="timeline-card-content">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center text-2xl flex-shrink-0">
                    🚀
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                      QA Engineer
                    </h3>
                    <p className="text-lg text-purple-600 dark:text-purple-400 font-semibold">
                      Prosol QSC
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 mb-6">
                  <Calendar className="w-4 h-4" />
                  <span>December 2023 - July 2025</span>
                </div>

                <ul className="space-y-3">
                  <li className="flex items-start gap-3 text-gray-700 dark:text-gray-300">
                    <span className="text-purple-600 dark:text-purple-400 text-xl flex-shrink-0">
                      ▹
                    </span>
                    <span>
                      Developed custom Cypress plugins for API validation, token
                      management, and dynamic test data mocks, accelerating test
                      execution and improving reliability across environments.
                    </span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-700 dark:text-gray-300">
                    <span className="text-purple-600 dark:text-purple-400 text-xl flex-shrink-0">
                      ▹
                    </span>
                    <span>
                      Improved E2E test execution speed by 35% using
                      parallelization, optimized selectors, and disciplined DOM
                      stability strategies.
                    </span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-700 dark:text-gray-300">
                    <span className="text-purple-600 dark:text-purple-400 text-xl flex-shrink-0">
                      ▹
                    </span>
                    <span>
                      Standardized test documentation (test cases, test suites,
                      bug reports) using Jira and Confluence, cutting triage
                      time by 15% and elevating team productivity.
                    </span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-700 dark:text-gray-300">
                    <span className="text-purple-600 dark:text-purple-400 text-xl flex-shrink-0">
                      ▹
                    </span>
                    <span>
                      Recognized by team leads for rapid skill
                      growth—progressing from intern to mid-level engineer
                      within 18 months, contributing to multiple
                      mission-critical cybersecurity releases.
                    </span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-700 dark:text-gray-300">
                    <span className="text-purple-600 dark:text-purple-400 text-xl flex-shrink-0">
                      ▹
                    </span>
                    <span>
                      Conducted interviews with 5–6 Junior/Intern QA candidates,
                      assessing testing fundamentals, API knowledge,
                      bug-reporting skills, and automation
                      readiness—contributing to improved team onboarding and
                      selection accuracy.
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Philosophy Section */}
        <div className="bg-gradient-to-r from-purple-100 to-blue-100 dark:from-purple-900/30 dark:to-blue-900/30 rounded-3xl p-12 text-center animate-fade-in">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
            My Testing Philosophy
          </h2>
          <p className="text-xl text-gray-700 dark:text-gray-300 leading-relaxed max-w-4xl mx-auto">
            "Quality is not just an act, it’s a habit. I approach every project
            with meticulous attention to detail, proactive problem-solving, and
            a commitment to delivering software that works flawlessly and
            provides an exceptional user experience"
          </p>
          <div className="mt-8 inline-block px-6 py-3 bg-white dark:bg-gray-800 rounded-full shadow-lg">
            <span className="text-gray-700 dark:text-gray-300 font-medium">
              - {personalInfo.name}, QA Engineer & Automation Specialist
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
